import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGoogle, FaGithub, FaLinkedin, FaMicrosoft } from 'react-icons/fa';
import { Building2, Mail, Lock, User, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import GridBackground from '../components/GridBackground';

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('signin');
  const [showPassword, setShowPassword] = useState(false);
  const [showSSOInput, setShowSSOInput] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    ssoEmail: ''
  });

  const testimonials = [
    {
      quote: "Intugle reduced our data product development time by 10x. The semantic layer is a game-changer.",
      name: "John Doe",
      role: "Head of Data, Fortune 500",
      initials: "JD"
    },
    {
      quote: "We went from months of data integration work to days. Intugle's AI agents understand our data better than we do.",
      name: "Sarah Chen",
      role: "VP of Analytics, TechCorp",
      initials: "SC"
    },
    {
      quote: "The best investment we made this year. Our data team now focuses on insights, not pipelines.",
      name: "Michael Roberts",
      role: "CTO, DataFirst Inc",
      initials: "MR"
    },
    {
      quote: "Finally, a platform that speaks both data and business. Our executives now make decisions in minutes, not weeks.",
      name: "Emily Watson",
      role: "Chief Data Officer, GlobalRetail",
      initials: "EW"
    },
    {
      quote: "Intugle transformed how we think about data products. It's like having a senior data engineer available 24/7.",
      name: "David Park",
      role: "Director of Engineering, FinanceAI",
      initials: "DP"
    }
  ];

  // Auto-rotate testimonials every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Get the query that was entered on the landing page
  const pendingQuery = location.state?.pendingQuery || '';

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // WorkOS will handle actual authentication
    // For now, redirect to dashboard with the pending query
    navigate('/dashboard', { state: { initialQuery: pendingQuery } });
  };

  const handleOAuthLogin = (provider) => {
    // WorkOS OAuth handling will go here
    console.log(`OAuth login with ${provider}`);
    // After successful OAuth, redirect to dashboard
    navigate('/dashboard', { state: { initialQuery: pendingQuery } });
  };

  const handleSSOLogin = (e) => {
    e.preventDefault();
    // WorkOS SSO handling will go here
    console.log(`SSO login for ${formData.ssoEmail}`);
    navigate('/dashboard', { state: { initialQuery: pendingQuery } });
  };

  const oauthProviders = [
    { id: 'google', name: 'Google', icon: FaGoogle },
    { id: 'github', name: 'GitHub', icon: FaGithub },
    { id: 'linkedin', name: 'LinkedIn', icon: FaLinkedin },
    { id: 'microsoft', name: 'Microsoft', icon: FaMicrosoft },
  ];

  return (
    <div className="min-h-screen flex bg-[#020617]">
      {/* Left Panel - Visual/Branding with Grid Background */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center">
        {/* Use same GridBackground as landing page */}
        <div className="absolute inset-0">
          <GridBackground />
        </div>
        
        <style>{`
          input:-webkit-autofill,
          input:-webkit-autofill:hover,
          input:-webkit-autofill:focus,
          input:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0 30px rgb(30 41 59 / 0.6) inset !important;
            -webkit-text-fill-color: white !important;
            transition: background-color 5000s ease-in-out 0s;
          }
        `}</style>
        
        {/* Content */}
        <div className="relative z-10 max-w-md px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <img src="/intugle-logo.svg" alt="Intugle" className="h-20 w-auto mx-auto mb-8" />
            <h1 className="text-3xl font-bold text-white mb-4">
              Transform your Data into{' '}
              <span className="block mt-2 bg-gradient-to-r from-blue-400 via-blue-500 to-indigo-500 bg-clip-text text-transparent">
                GenAI Applications
              </span>
            </h1>
          </motion.div>

          {/* Testimonial/Stats with Slide Animation */}
          <div className="mt-12 p-6 rounded-xl bg-slate-900/90 shadow-[0_0_20px_rgba(59,130,246,0.25)] backdrop-blur-sm overflow-hidden min-h-[180px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentTestimonial}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
              >
                <p className="text-gray-300 italic mb-4">
                  "{testimonials[currentTestimonial].quote}"
                </p>
                <div className="flex items-center justify-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-semibold">
                    {testimonials[currentTestimonial].initials}
                  </div>
                  <div className="text-left">
                    <p className="text-white font-medium text-sm">{testimonials[currentTestimonial].name}</p>
                    <p className="text-gray-500 text-xs">{testimonials[currentTestimonial].role}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
            
            {/* Dots indicator */}
            <div className="flex justify-center gap-1 mt-4">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-1 h-1 rounded-full transition-all duration-300 ${
                    index === currentTestimonial 
                      ? 'bg-blue-500 w-2' 
                      : 'bg-slate-600 hover:bg-slate-500'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8 lg:p-12 bg-[#020617] relative z-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img src="/intugle-logo.svg" alt="Intugle" className="h-14 w-auto mx-auto mb-4" />
          </div>

          {/* Auth Card */}
          <div className="backdrop-blur-xl bg-slate-950/40 border border-slate-700/50 rounded-2xl p-8 shadow-2xl">
            {/* Tabs */}
            <div className="flex mb-8 bg-slate-900/50 rounded-lg p-1">
              <button
                data-testid="signin-tab"
                onClick={() => { setActiveTab('signin'); setShowSSOInput(false); }}
                className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                  activeTab === 'signin'
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Sign In
              </button>
              <button
                data-testid="signup-tab"
                onClick={() => { setActiveTab('signup'); setShowSSOInput(false); }}
                className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                  activeTab === 'signup'
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Sign Up
              </button>
            </div>

            {/* Fixed height content area */}
            <div className="h-[460px] w-full">
            <AnimatePresence mode="wait">
              {!showSSOInput ? (
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.2 }}
                  className="h-full w-full flex flex-col items-stretch"
                >
                  {/* OAuth Buttons - Only show for Sign In */}
                  {activeTab === 'signin' && (
                    <>
                      <div className="grid grid-cols-2 gap-3 mb-6">
                        {oauthProviders.map((provider) => (
                          <Button
                            key={provider.id}
                            data-testid={`oauth-${provider.id}`}
                            variant="outline"
                            onClick={() => handleOAuthLogin(provider.id)}
                            className="bg-slate-800/60 border-0 text-gray-300 hover:bg-white hover:text-slate-900 transition-all duration-200"
                          >
                            <provider.icon className="w-4 h-4 mr-2" />
                            {provider.name}
                          </Button>
                        ))}
                      </div>

                      {/* Divider */}
                      <div className="relative my-6 flex items-center">
                        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-600/50 to-slate-600/50"></div>
                        <span className="px-4 text-xs text-gray-500">or continue with email</span>
                        <div className="flex-1 h-px bg-gradient-to-l from-transparent via-slate-600/50 to-slate-600/50"></div>
                      </div>
                    </>
                  )}

                  {/* Email/Password Form */}
                  <form onSubmit={handleSubmit} className="space-y-4 w-full">
                    {activeTab === 'signup' && (
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-gray-300 text-sm">Full Name</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                          <Input
                            id="name"
                            name="name"
                            data-testid="signup-name-input"
                            type="text"
                            placeholder="John Doe"
                            value={formData.name}
                            onChange={handleInputChange}
                            className="pl-10 bg-slate-800/60 border border-transparent text-white placeholder:text-gray-500 focus:border-blue-500/50 focus:ring-0"
                          />
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-gray-300 text-sm">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <Input
                          id="email"
                          name="email"
                          data-testid="email-input"
                          type="email"
                          placeholder="you@company.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="pl-10 bg-slate-800/60 border border-transparent text-white placeholder:text-gray-500 focus:border-blue-500/50 focus:ring-0"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password" className="text-gray-300 text-sm">Password</Label>
                        {activeTab === 'signin' && (
                          <button
                            type="button"
                            data-testid="forgot-password-link"
                            className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                          >
                            Forgot password?
                          </button>
                        )}
                      </div>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <Input
                          id="password"
                          name="password"
                          data-testid="password-input"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className="pl-10 pr-10 bg-slate-800/60 border border-transparent text-white placeholder:text-gray-500 focus:border-blue-500/50 focus:ring-0"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      data-testid="submit-btn"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 transition-all duration-200 hover:-translate-y-0.5 shadow-lg shadow-blue-500/20"
                    >
                      {activeTab === 'signin' ? 'Sign In' : 'Create Account'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </form>

                  {/* SSO Option - Only show for Sign In */}
                  {activeTab === 'signin' && (
                    <div className="mt-auto w-full">
                      {/* Border line */}
                      <div className="w-full border-t border-slate-800/40 mb-6"></div>
                      <button
                        data-testid="sso-toggle"
                        onClick={() => setShowSSOInput(true)}
                        className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-slate-800/60 text-gray-400 hover:text-white hover:bg-slate-700/60 transition-all duration-200"
                      >
                        <Building2 className="w-4 h-4" />
                        <span className="text-sm font-medium">Sign in with SSO</span>
                      </button>
                      <p className="text-center text-xs text-gray-500 mt-3">
                        For enterprise single sign-on
                      </p>
                    </div>
                  )}

                  {/* Spacer for Sign Up to maintain consistent height */}
                  {activeTab === 'signup' && <div className="mt-auto" />}
                </motion.div>
              ) : (
                <motion.div
                  key="sso-form"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="h-full flex flex-col"
                >
                  {/* SSO Form */}
                  <div className="text-center mb-6">
                    <Building2 className="w-12 h-12 text-blue-500 mx-auto mb-3" />
                    <h3 className="text-xl font-semibold text-white">Enterprise SSO</h3>
                    <p className="text-gray-400 text-sm mt-1">Sign in with your company's identity provider</p>
                  </div>

                  <form onSubmit={handleSSOLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="ssoEmail" className="text-gray-300 text-sm">Work Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <Input
                          id="ssoEmail"
                          name="ssoEmail"
                          data-testid="sso-email-input"
                          type="email"
                          placeholder="you@company.com"
                          value={formData.ssoEmail}
                          onChange={handleInputChange}
                          className="pl-10 bg-slate-800/60 border border-transparent text-white placeholder:text-gray-500 focus:border-blue-500/50 focus:ring-0"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      data-testid="sso-submit-btn"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 transition-all duration-200 hover:-translate-y-0.5 shadow-lg shadow-blue-500/20"
                    >
                      Continue with SSO
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </form>

                  <button
                    data-testid="back-to-login"
                    onClick={() => setShowSSOInput(false)}
                    className="w-full mt-4 text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    Back to other sign in options
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
            </div>
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-gray-500 mt-6">
            By continuing, you agree to Intugle's{' '}
            <a href="#" className="text-blue-400 hover:text-blue-300">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="text-blue-400 hover:text-blue-300">Privacy Policy</a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default AuthPage;
